/*
 * Technology Readiness Audit — the attester's domain rules.
 *
 * The person this serves is a fractional CTO signing their name to an
 * agent-produced audit, not an operator of the platform. So everything here is
 * expressed in review vocabulary (report, finding, correction, attest,
 * decline, fee) and nothing here surfaces an internal id unless a human would
 * quote it back to us.
 *
 * Two contracts are mirrored, both frozen:
 *
 *  - the wire, from bluebear-gateway docs/marketplace-seams-2026-09-06.md —
 *    snake_case in both directions (see CLAUDE.md, "Wire format");
 *  - the report markdown, from bluebear-mcps
 *    src/mcps/readiness-audit/report.ts (`renderMarkdown` / `renderFinding`).
 *
 * The parser below is written against that renderer, not against markdown in
 * general: a heading it does not recognise is left as prose rather than
 * guessed at, because a reviewer inventing a finding is worse than a reviewer
 * reading a paragraph.
 */

import { formatUsd, marketplaceAccessGranted, marketplaceNumber, summarizeMarketplaceEarnings } from './marketplaceShared';

export { formatUsd } from './marketplaceShared';

/** The gateway permission that governs this whole surface. */
export const MARKETPLACE_REVIEW_PERMISSION = 'review_marketplace_executions';
/** The tenant-feature key, for when the grant is projected that way instead. */
export const MARKETPLACE_REVIEW_FEATURE = 'marketplace_review';

/**
 * May this viewer see the review queue at all?
 *
 * Deliberately NO role fallback. The gateway keeps
 * `review_marketplace_executions` out of ROLE_PERMISSIONS on purpose — handing
 * it to every `tenant`-role user is the opposite of what an attestation gate is
 * for — so the UI does not widen it either. Until the grant is projected onto
 * the session user, the nav entry stays hidden and the route is reachable only
 * by its URL, where the gateway is still the thing that says no.
 */
export function canReviewMarketplaceExecutions(user) {
  return marketplaceAccessGranted(user, MARKETPLACE_REVIEW_PERMISSION, MARKETPLACE_REVIEW_FEATURE);
}

const SIZE_LABELS = { short: 'Short', standard: 'Standard', deep: 'Deep' };

/** "standard" → "Standard". An unknown size is shown as sent, never dropped. */
export function sizeLabel(sizeCode) {
  if (!sizeCode) return '—';
  return SIZE_LABELS[String(sizeCode).toLowerCase()] || String(sizeCode);
}

/**
 * A readable title from the offer slug.
 *
 * The queue projects `offer_slug`, not a title (no internal ids are exposed and
 * the title is not among the projected columns), so this derives one rather
 * than inventing a different name for the same offer.
 */
export function offerTitleFromSlug(slug) {
  if (!slug) return 'Readiness report';
  return String(slug)
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** A number, or null. Never a zero conjured out of a missing field. */
function numberOrNull(value) {
  return marketplaceNumber(value);
}

/**
 * One queue row, wire → screen.
 *
 * `buyer_label`, `workspace_id` and a readiness score are read if the gateway
 * sends them and left null if it does not — see the PR body for which of those
 * the frozen contract currently omits. A null renders as "—", never as a guess.
 */
export function normalizeQueueRow(row) {
  if (!row) return null;
  const offerSlug = row.offer_slug || null;
  const findings = row.findings_summary || {};
  return {
    executionId: row.execution_id || null,
    status: row.status || null,
    offerSlug,
    offerVersion: row.offer_version || null,
    title: offerTitleFromSlug(offerSlug),
    sizeCode: row.size_code || null,
    sizeLabel: sizeLabel(row.size_code),
    submittedAt: row.created_at || null,
    releasedAt: row.released_at || null,
    // The gateway names it both ways across the contract; take either.
    reportPath: row.result_document_id || row.report_path || null,
    workspaceId: row.workspace_id || null,
    assigneeUserId: row.review_assignee_user_id || null,
    attestedByUserId: row.attested_by_user_id || null,
    buyerLabel: row.buyer_label || row.tenant_slug || row.tenant_name || null,
    creditsCharged: numberOrNull(row.credits_charged),
    ruleIds: Array.isArray(findings.rule_ids) ? findings.rule_ids : [],
    validatorId: findings.validator_id || null,
    evaluationSetId: findings.evaluation_set_id || null,
    readinessScore: numberOrNull(findings.readiness_score),
  };
}

export function normalizeQueue(payload) {
  const rows = Array.isArray(payload?.executions) ? payload.executions : [];
  return rows.map(normalizeQueueRow).filter(Boolean);
}

/** Is this viewer the person the gateway will let act on the report? */
export function isReviewAssignee(row, userId) {
  if (!row) return false;
  if (!row.assigneeUserId) return true; // unassigned — the gateway lets anyone tenant-scoped act
  return Boolean(userId) && row.assigneeUserId === userId;
}

// ── The report ──────────────────────────────────────────────────────────────

/** Severities the readiness runner emits, weakest claim last. */
export const SEVERITY_OPTIONS = ['strong', 'acceptable', 'weak', 'risky', 'critical'];

const SEVERITY_TONES = {
  strong: 'bg-emerald-900/40 text-emerald-300 border border-emerald-800/60',
  acceptable: 'bg-blue-900/40 text-blue-300 border border-blue-800/60',
  weak: 'bg-amber-900/40 text-amber-300 border border-amber-800/60',
  risky: 'bg-orange-900/40 text-orange-300 border border-orange-800/60',
  critical: 'bg-red-900/40 text-red-300 border border-red-800/60',
};

export function severityTone(severity) {
  return SEVERITY_TONES[String(severity || '').toLowerCase()]
    || 'bg-gray-700/60 text-gray-300 border border-gray-600/60';
}

const FINDING_HEADING = /^####\s+(\S+)\s+—\s+(.*)$/;
const SECTION_HEADING = /^##\s+(.*)$/;
const SEVERITY_LINE = /^\*\*Severity:\*\*\s*(.*)$/;
const RECOMMENDATION_LINE = /^\*\*Recommendation:\*\*\s*(.*)$/;
const EVIDENCE_LINE = /^\*\*Evidence:\*\*\s*$/;
const BULLET = /^-\s+(.*)$/;
const COMPOSITE = /\*\*Composite:\s*([^/*]+?)\s*\/\s*(\d+)\*\*/;

/**
 * A finding reference stable enough to correct against.
 *
 * The gateway's example is `RA-SEC-001#3`: a rule id plus which occurrence of
 * that rule this is, counted in document order. The renderer does not number
 * findings, so we count them — same input, same ref, every time the report is
 * opened.
 */
export function findingRef(ruleId, occurrence) {
  return `${ruleId}#${occurrence}`;
}

/**
 * Parse the readiness report markdown into sections and findings.
 *
 * Returns the prose untouched alongside the structure, so the page can render
 * the report as the runner wrote it and still put a Correct control beside each
 * finding.
 */
export function parseReadinessReport(markdown) {
  const text = typeof markdown === 'string' ? markdown : '';
  const lines = text.split('\n');

  const report = {
    title: '',
    meta: [],
    score: { composite: null, outOf: null, text: '' },
    sections: [],
    findings: [],
  };

  const occurrences = new Map();
  let section = null;
  let finding = null;
  let inEvidence = false;

  const closeFinding = () => {
    if (finding) {
      if (section) section.findings.push(finding);
      report.findings.push(finding);
    }
    finding = null;
    inEvidence = false;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.startsWith('# ') && !report.title) {
      report.title = line.slice(2).trim();
      continue;
    }

    const findingMatch = line.match(FINDING_HEADING);
    if (findingMatch) {
      closeFinding();
      const ruleId = findingMatch[1];
      const occurrence = (occurrences.get(ruleId) || 0) + 1;
      occurrences.set(ruleId, occurrence);
      finding = {
        ruleId,
        occurrence,
        findingRef: findingRef(ruleId, occurrence),
        title: findingMatch[2].trim(),
        severity: '',
        evidence: [],
        recommendation: '',
        section: section ? section.title : '',
      };
      continue;
    }

    const sectionMatch = line.match(SECTION_HEADING);
    if (sectionMatch) {
      closeFinding();
      section = { title: sectionMatch[1].trim(), summary: '', findings: [] };
      report.sections.push(section);
      continue;
    }

    if (finding) {
      const severity = line.match(SEVERITY_LINE);
      if (severity) {
        finding.severity = severity[1].trim();
        continue;
      }
      if (EVIDENCE_LINE.test(line)) {
        inEvidence = true;
        continue;
      }
      const recommendation = line.match(RECOMMENDATION_LINE);
      if (recommendation) {
        finding.recommendation = recommendation[1].trim();
        inEvidence = false;
        continue;
      }
      const bullet = line.match(BULLET);
      if (inEvidence && bullet) {
        finding.evidence.push(bullet[1].trim());
        continue;
      }
      if (line.trim()) inEvidence = false;
      continue;
    }

    const composite = line.match(COMPOSITE);
    if (composite) {
      report.score.text = line.trim();
      report.score.composite = numberOrNull(composite[1]);
      report.score.outOf = numberOrNull(composite[2]);
      continue;
    }

    if (!section && line.startsWith('- **')) {
      report.meta.push(line.replace(/^-\s+/, '').trim());
      continue;
    }

    // A section's summary is its first prose line — not a table row, a bullet
    // or a bolded label, all of which the renderer also emits under a heading.
    if (section && !section.summary && section.findings.length === 0) {
      const candidate = line.trim();
      if (candidate && !/^[|\-*>]/.test(candidate) && !candidate.startsWith('**')) {
        section.summary = candidate;
      }
    }
  }

  closeFinding();
  return report;
}

// ── Corrections ─────────────────────────────────────────────────────────────

/*
 * The gateway refuses a correction carrying source: any of these keys at any
 * depth, or any string with a code fence, is 422 CODE_EXCERPT_REJECTED and
 * nothing is written. Mirrored here so the reviewer is told at the form rather
 * than after a round trip — the gateway remains the enforcer.
 */
export const BANNED_CORRECTION_KEYS = [
  'code', 'code_excerpt', 'excerpt', 'snippet', 'source', 'source_code',
  'diff', 'patch', 'file_contents', 'content',
];

const BANNED_KEY_SET = new Set(BANNED_CORRECTION_KEYS);

/** The offending path, or null when the value carries no code excerpt. */
export function findCodeExcerpt(value, path = 'body') {
  if (typeof value === 'string') {
    return value.includes('```') ? `${path} contains a code fence` : null;
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      const hit = findCodeExcerpt(value[i], `${path}[${i}]`);
      if (hit) return hit;
    }
    return null;
  }
  if (value && typeof value === 'object') {
    for (const [key, item] of Object.entries(value)) {
      if (BANNED_KEY_SET.has(key.toLowerCase())) {
        return `${path}.${key} is not an accepted field`;
      }
      const hit = findCodeExcerpt(item, `${path}.${key}`);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * Build the correction body from what the reviewer typed.
 *
 * `affected_rule_ids` is prefilled with the finding's own rule id — a reviewer
 * correcting one finding is, by default, saying something about that one rule.
 */
export function buildCorrectionPayload({ finding, severity, recommendation, reason } = {}) {
  if (!finding || !finding.findingRef) {
    return { ok: false, error: 'Pick a finding to correct first.' };
  }
  const nextSeverity = String(severity ?? finding.severity ?? '').trim();
  const nextRecommendation = String(recommendation ?? '').trim();
  const why = String(reason ?? '').trim();

  const severityChanged = nextSeverity && nextSeverity !== finding.severity;
  const recommendationChanged =
    nextRecommendation && nextRecommendation !== finding.recommendation;

  if (!severityChanged && !recommendationChanged) {
    return { ok: false, error: 'Change the severity or the recommendation before saving.' };
  }
  if (!why) {
    return { ok: false, error: 'Say why — the reason is recorded with the correction.' };
  }

  const payload = {
    finding_ref: finding.findingRef,
    original: {
      severity: finding.severity || null,
      recommendation: finding.recommendation || null,
    },
    corrected: {
      severity: severityChanged ? nextSeverity : finding.severity || null,
      recommendation: recommendationChanged
        ? nextRecommendation
        : finding.recommendation || null,
    },
    reason: why,
    affected_rule_ids: finding.ruleId ? [finding.ruleId] : [],
  };

  const excerpt = findCodeExcerpt(payload);
  if (excerpt) {
    return {
      ok: false,
      error: `Corrections record findings, not code — ${excerpt}. Describe the problem instead of pasting the source.`,
    };
  }

  return { ok: true, payload };
}

// ── Earnings ────────────────────────────────────────────────────────────────

const BASIS_LABELS = {
  attestation_fee: 'Attestation fee',
  publisher_remainder: 'Publisher share',
};

export function basisLabel(basis) {
  if (!basis) return '—';
  return BASIS_LABELS[basis] || String(basis).replace(/_/g, ' ');
}

/**
 * Fees for the caller, split accrued vs paid.
 *
 * `total_accrued_usd` is computed by the gateway; it is preferred over our own
 * sum when present so the two never disagree on screen.
 */
export function summarizeEarnings(payload) {
  const summary = summarizeMarketplaceEarnings(payload);
  const rows = summary.rows.map((row) => ({
    ...row,
    offerTitle: offerTitleFromSlug(row.offerSlug),
    basisLabel: basisLabel(row.basis),
  }));
  return {
    rows,
    accruedUsd: summary.accruedUsd,
    paidUsd: summary.paidUsd,
  };
}

// ── Errors ──────────────────────────────────────────────────────────────────

const ERROR_NOTICES = {
  NOT_REVIEW_ASSIGNEE: 'This report is assigned to another reviewer.',
  CODE_EXCERPT_REJECTED:
    'Corrections record findings, not code. Describe the problem instead of pasting the source.',
  EXECUTION_NOT_RELEASABLE: 'This report can no longer be released — it was already declined or it failed.',
  EXECUTION_NOT_AWAITING_REVIEW: 'This report is not awaiting review any more. Refresh the queue.',
  EXECUTION_ALREADY_SETTLED: 'This report is already settled.',
  ALLOCATION_RULE_MISSING: 'No fee split is in force for this offer version, so nothing can be released yet. Ask the publisher.',
  ALLOCATION_EXCEEDS_PRICE: 'The attestation fee is larger than what the buyer was charged. Ask the publisher to fix the split.',
  EXECUTION_NOT_FOUND: 'That report is not in your queue.',
  TENANT_CONTEXT_REQUIRED: 'Your session is not scoped to a workspace tenant. Sign in again.',
};

/** Turn a gateway error into one sentence a reviewer can act on. */
export function reviewErrorNotice(err, fallback = 'Something went wrong.') {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  if (code && ERROR_NOTICES[code]) {
    return { code, status: status || null, message: ERROR_NOTICES[code] };
  }
  if (status === 403) {
    return { code: code || 'FORBIDDEN', status, message: ERROR_NOTICES.NOT_REVIEW_ASSIGNEE };
  }
  if (status === 401) {
    return { code: code || 'UNAUTHENTICATED', status, message: 'Your session has expired. Sign in again.' };
  }
  const message = err?.response?.data?.error || err?.message || fallback;
  return { code: code || null, status: status || null, message };
}

/** True when the error is the gateway saying "not your report". */
export function isNotAssigneeError(err) {
  const notice = reviewErrorNotice(err);
  return notice.code === 'NOT_REVIEW_ASSIGNEE' || notice.status === 403;
}
